import axios from 'axios';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const { hostname, port, protocol } = window.location;
    const host = hostname || '';
    const portStr = port || '';

    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3200/api';
    }

    // Frontend on :3100 — API on :3200 (direct ports, no nginx /api path).
    if (portStr === '3100') {
      return `${protocol}//${host}:3200/api`;
    }

    // Browser same-origin /api (nginx on :8888, or production host with /api proxy).
    const origin = `${protocol}//${host}${portStr ? `:${portStr}` : ''}`;
    return `${origin}/api`;
  }

  const env = process.env.REACT_APP_API_URL;
  if (env && typeof env === 'string' && env.trim()) {
    const cleaned = env.trim().replace(/\/+$/, '');
    if (/^https?:\/\/[^/:\s]+/.test(cleaned)) {
      return cleaned + (cleaned.endsWith('/api') ? '' : '/api');
    }
  }
  return 'http://localhost:3200/api';
};
const API_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

const isPerfDebug = () => typeof localStorage !== 'undefined' && localStorage.getItem('cc_debug_perf') === '1';

api.interceptors.request.use((config) => {
  if (isPerfDebug()) config.metadata = { startTime: Date.now(), url: config.url || config.baseURL };
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // FormData uploads must not force JSON Content-Type
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    try { delete config.headers['Content-Type']; } catch (_) {}
  }
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.email) {
      config.headers['X-User-Email'] = user.email;
    }
  } catch (_) {}
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (isPerfDebug() && response.config?.metadata?.startTime) {
      const ms = Date.now() - response.config.metadata.startTime;
      const url = response.config.url || response.request?.responseURL || 'unknown';
      console.log(`[API] ${response.config.method?.toUpperCase()} ${url} → ${ms}ms`);
    }
    return response;
  },
  (error) => {
    if (isPerfDebug() && error.config?.metadata?.startTime) {
      const ms = Date.now() - error.config.metadata.startTime;
      const url = error.config?.url || 'unknown';
      console.warn(`[API] ${error.config?.method?.toUpperCase()} ${url} → ${ms}ms (failed)`);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  changeRequiredPassword: (newPassword) => api.post('/auth/change-required-password', { newPassword }),
  forgotPassword: (email) => api.post('/auth/forgot-password/request', { email }),
  confirmForgotPassword: (data) => api.post('/auth/forgot-password/confirm', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  adminResetPassword: (userId, newPassword) => api.post('/auth/reset-password', { userId, newPassword }),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  },
  acceptInvitation: (data) => api.post('/auth/accept-invitation', data),
};


export const reportsAPI = {
  getBulkData: (params) => api.get('/reports/data', { params }),
  getSummaryData: (params) => api.get('/reports/data/summary', { params }),
  getRestData: (params) => api.get('/reports/data/rest', { params }),
  getCorrespondenceExport: (params) => api.get('/reports/correspondence-export', { params }),
  getSeniorManagement: async (params) => {
    try {
      return await api.get('/reports/senior-management', { params });
    } catch (e) {
      if (e.response?.status === 404) {
        return api.get('/reports/senior_management', { params });
      }
      throw e;
    }
  },
};

export const correspondenceAPI = {
  getChains: (params) => api.get('/correspondence/chains', { params }),
  getChainsWithEntries: (params) => api.get('/correspondence/chains', { params: { ...params, includeEntries: '1' } }),
  getChainsPaginated: (params) => api.get('/correspondence/chains', { params }),
  getSummary: (params) => api.get('/correspondence/summary', { params }),
  getLocationBlocks: (params) => api.get('/correspondence/location-blocks', { params }),
  getChainById: (id) => api.get(`/correspondence/chains/${id}`),
  createChain: (data) => api.post('/correspondence/chains', data),
  updateChain: (id, data) => api.put(`/correspondence/chains/${id}`, data),
  deleteChain: (id) => api.delete(`/correspondence/chains/${id}`),
  getEntries: (chainId) => api.get(`/correspondence/chains/${chainId}/entries`),
  createEntry: (chainId, data) => api.post(`/correspondence/chains/${chainId}/entries`, data, { timeout: 300000 }),
  updateEntry: (chainId, entryId, data) => api.put(`/correspondence/chains/${chainId}/entries/${entryId}`, data, { timeout: 300000 }),
  deleteEntry: (chainId, entryId) => api.delete(`/correspondence/chains/${chainId}/entries/${entryId}`),
  searchSubject: (projectId, query) => api.get('/correspondence/search', { params: { projectId, query } }),
  getChainFlags: (id) => api.get(`/correspondence/chains/${id}/flags`),
  saveChainFlags: (id, data) => api.post(`/correspondence/chains/${id}/flags`, data),
  toggleImportant: (id) => api.post(`/correspondence/chains/${id}/toggle-important`),
  togglePin: (id) => api.post(`/correspondence/chains/${id}/toggle-pin`),
  getSavedViews: () => api.get('/correspondence/saved-views'),
  createSavedView: (data) => api.post('/correspondence/saved-views', data),
  updateSavedView: (id, data) => api.put(`/correspondence/saved-views/${id}`, data),
  deleteSavedView: (id) => api.delete(`/correspondence/saved-views/${id}`),
  setSavedViewDefault: (id) => api.post(`/correspondence/saved-views/${id}/set-default`),
  duplicateSavedView: (id) => api.post(`/correspondence/saved-views/${id}/duplicate`),
  pinSavedView: (id, data) => api.post(`/correspondence/saved-views/${id}/pin`, data),
};

export const raBillsAPI = {
  getAll: (params) => api.get('/ra-bills', { params }),
  getById: (id) => api.get(`/ra-bills/${id}`),
  create: (data) => api.post('/ra-bills', data),
  update: (id, data) => api.put(`/ra-bills/${id}`, data),
  delete: (id) => api.delete(`/ra-bills/${id}`),
  resolveRule: (params) => api.get('/ra-bills/rules/resolve', { params }),
};

export const raBillTemplatesAPI = {
  list: (params) => api.get('/admin/ra-bill-templates', { params }),
  getById: (id) => api.get(`/admin/ra-bill-templates/${id}`),
  create: (data) => api.post('/admin/ra-bill-templates', data),
  update: (id, data) => api.put(`/admin/ra-bill-templates/${id}`, data),
  remove: (id) => api.delete(`/admin/ra-bill-templates/${id}`),
  clone: (id, data) => api.post(`/admin/ra-bill-templates/${id}/clone`, data),
  createVersion: (id, data) => api.post(`/admin/ra-bill-templates/${id}/create-version`, data),
  validate: (id, data) => api.post(`/admin/ra-bill-templates/${id}/validate`, data),
  preview: (id, data) => api.post(`/admin/ra-bill-templates/${id}/preview`, data),
  publish: (id, data) => api.post(`/admin/ra-bill-templates/${id}/publish`, data),
  audit: (params) => api.get('/admin/ra-bill-templates-audit', { params }),
};

export const bankGuaranteesAPI = {
  getAll: (params) => api.get('/bank-guarantees', { params }),
  getById: (id) => api.get(`/bank-guarantees/${id}`),
  create: (data) => api.post('/bank-guarantees', data),
  update: (id, data) => api.put(`/bank-guarantees/${id}`, data),
  delete: (id) => api.delete(`/bank-guarantees/${id}`),
};

export const milestonesAPI = {
  getAll: (params) => api.get('/milestones', { params }),
  getById: (id) => api.get(`/milestones/${id}`),
  create: (data) => api.post('/milestones', data),
  update: (id, data) => api.put(`/milestones/${id}`, data),
  delete: (id) => api.delete(`/milestones/${id}`),
  createShiftRequest: (milestoneId, data) => api.post(`/milestones/${milestoneId}/shift-requests`, data),
};

export const drawingsAPI = {
  getAll: (params) => api.get('/drawings', { params }),
  getById: (id) => api.get(`/drawings/${id}`),
  create: (data) => api.post('/drawings', data),
  update: (id, data) => api.put(`/drawings/${id}`, data),
};

export const deviationsAPI = {
  getAll: (params) => api.get('/deviations', { params }),
  getById: (id) => api.get(`/deviations/${id}`),
  create: (data) => api.post('/deviations', data),
  update: (id, data) => api.put(`/deviations/${id}`, data),
  delete: (id) => api.delete(`/deviations/${id}`),
};

export const qaqcAPI = {
  getAll: (params) => api.get('/qaqc', { params }),
  getById: (id) => api.get(`/qaqc/${id}`),
  create: (data) => api.post('/qaqc', data),
  update: (id, data) => api.put(`/qaqc/${id}`, data),
  delete: (id) => api.delete(`/qaqc/${id}`),
};

export const clearancesAPI = {
  getAll: (params) => api.get('/clearances', { params }),
  getById: (id) => api.get(`/clearances/${id}`),
  create: (data) => api.post('/clearances', data),
  update: (id, data) => api.put(`/clearances/${id}`, data),
  delete: (id) => api.delete(`/clearances/${id}`),
};

export const scopeAPI = {
  getProjectHandovers: (params) => api.get('/project-handovers', { params }),
  createProjectHandover: (data) => api.post('/project-handovers', data),
  updateProjectHandover: (id, data) => api.put(`/project-handovers/${id}`, data),
  getSiteOffices: (params) => api.get('/site-offices', { params }),
  createSiteOffice: (data) => api.post('/site-offices', data),
  updateSiteOffice: (id, data) => api.put(`/site-offices/${id}`, data),
  deleteSiteOffice: (id) => api.delete(`/site-offices/${id}`),
  getClientOffices: (params) => api.get('/client-offices', { params }),
  createClientOffice: (data) => api.post('/client-offices', data),
  updateClientOffice: (id, data) => api.put(`/client-offices/${id}`, data),
  deleteClientOffice: (id) => api.delete(`/client-offices/${id}`),
};

export const adminAPI = {
  getProjects: () => api.get('/admin/projects'),
  getProjectById: (id) => api.get(`/admin/projects/${id}`),
  createProject: (data) => api.post('/admin/projects', data),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  getDepartments: (params) => api.get('/admin/departments', { params }),
  getDepartmentById: (id) => api.get(`/admin/departments/${id}`),
  createDepartment: (data) => api.post('/admin/departments', data),
  updateDepartment: (id, data) => api.put(`/admin/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/admin/departments/${id}`),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  getRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRole: (id, data) => api.put(`/admin/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`),
  getPermissions: () => api.get('/admin/permissions'),
  updateRolePermissions: (id, permissionIds) => api.put(`/admin/roles/${id}/permissions`, { permissionIds }),
  updateUserRoles: (id, role_codes) => api.put(`/admin/users/${id}/roles`, { role_codes }),
  assignProjectUsers: (projectId, userIds) => api.put(`/admin/projects/${projectId}/assign-users`, { userIds }),
  uploadScopeAttachment: (projectId, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/projects/${projectId}/scope-attachments`, fd, {
      timeout: 600000,
      onUploadProgress: (e) => {
        if (typeof onProgress === 'function' && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    });
  },
  deleteScopeAttachment: (projectId, filename) =>
    api.delete(`/admin/projects/${projectId}/scope-attachments/${encodeURIComponent(filename)}`),
  getScopeAttachmentUrl: (projectId, filename) =>
    `${API_URL}/admin/projects/${projectId}/scope-attachments/${encodeURIComponent(filename)}`,
  fetchScopeAttachment: (projectId, filename, download = false) =>
    api.get(`${API_URL}/admin/projects/${projectId}/scope-attachments/${encodeURIComponent(filename)}${download ? '?download=1' : ''}`, { responseType: 'blob', timeout: 600000 }),
  importMockData: (data) => api.post('/admin/import-mock-data', data),
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getConsultants: () => api.get('/admin/consultants'),
  createConsultant: (data) => api.post('/admin/consultants', data),
  updateConsultant: (id, data) => api.put(`/admin/consultants/${id}`, data),
  deleteConsultant: (id) => api.delete(`/admin/consultants/${id}`),
  getAssignments: () => api.get('/admin/assignments'),
  createAssignment: (data) => api.post('/admin/assignments', data),
  updateAssignment: (id, data) => api.put(`/admin/assignments/${id}`, data),
  deleteAssignment: (id) => api.delete(`/admin/assignments/${id}`),
  getConsultantAssignments: () => api.get('/admin/consultant-assignments'),
  createConsultantAssignment: (data) => api.post('/admin/consultant-assignments', data),
  deleteConsultantAssignment: (id) => api.delete(`/admin/consultant-assignments/${id}`),
  getUsersAccessOverview: () => api.get('/admin/users-access/overview'),
  provisionUser: (data) => api.post('/admin/users/provision', data),
  inviteUser: (id, data = {}) => api.post(`/admin/users/${id}/invite`, data),
  getInvitePreview: (id, params) => api.get(`/admin/users/${id}/invite/preview`, { params }),
  getUserInvitations: (id) => api.get(`/admin/users/${id}/invitations`),
  getInvitations: (params) => api.get('/admin/invitations', { params }),
  cancelInvitation: (id) => api.post(`/admin/invitations/${id}/cancel`),
  lockUser: (id) => api.post(`/admin/users/${id}/lock`),
  unlockUser: (id) => api.post(`/admin/users/${id}/unlock`),
  deactivateUser: (id) => api.post(`/admin/users/${id}/deactivate`),
  reactivateUser: (id) => api.post(`/admin/users/${id}/reactivate`),
  getConfigFlags: () => api.get('/admin/config/flags'),
  seedConfigInventory: () => api.post('/admin/config/seed'),
  getConfigPages: () => api.get('/admin/config/pages'),
  getConfigPage: (pageKey) => api.get(`/admin/config/pages/${pageKey}`),
  getConfigForm: (formId) => api.get(`/admin/config/forms/${formId}`),
  getConfigFields: (params) => api.get('/admin/config/fields', { params }),
  getConfigKpis: (params) => api.get('/admin/config/kpis', { params }),
  getConfigKpiDataSources: () => api.get('/admin/config/kpi-data-sources'),
  createConfigKpi: (data) => api.post('/admin/config/kpis', data),
  updateConfigKpi: (id, data) => api.put(`/admin/config/kpis/${id}`, data),
  deleteConfigKpi: (id, hard = false) => api.delete(`/admin/config/kpis/${id}`, { params: hard ? { hard: 1 } : {} }),
  saveConfigKpiVisibility: (id, visibility) => api.put(`/admin/config/kpis/${id}/visibility`, { visibility }),
  previewConfigKpi: (id, data = {}) => api.post(`/admin/config/kpis/${id}/preview`, data),
  previewConfigKpiDraft: (data) => api.post('/admin/config/kpis/preview-draft', data),
  getRuntimeKpis: (params) => api.get('/config/kpis/runtime', { params }),
  getConfigVersions: () => api.get('/admin/config/versions'),
  getConfigAudit: () => api.get('/admin/config/audit'),
  getConfigDropdownMasters: (params) => api.get('/admin/config/dropdown-masters', { params }),
  getConfigValidationRules: (params) => api.get('/admin/config/validation-rules', { params }),
  getRuntimeFormConfig: (pageKey, formKey, params = {}) => api.get(`/config/runtime/pages/${pageKey}/forms/${formKey}`, { params }),
  getConfigFieldPermissions: (id) => api.get(`/admin/config/fields/${id}/permissions`),
  saveConfigFieldPermissions: (id, permissions) => api.put(`/admin/config/fields/${id}/permissions`, { permissions }),
  getConfigDropdownMaster: (masterKey, params) => api.get(`/admin/config/dropdown-masters/${masterKey}`, { params }),
  createConfigDropdownMaster: (data) => api.post('/admin/config/dropdown-masters', data),
  updateConfigDropdownMaster: (id, data) => api.put(`/admin/config/dropdown-masters/${id}`, data),
  upsertConfigDropdownOption: (masterId, data) => api.post(`/admin/config/dropdown-masters/${masterId}/options`, data),
  deleteConfigDropdownOption: (id) => api.delete(`/admin/config/dropdown-options/${id}`),
  getRuntimeDropdownOptions: (masterKey, params) => api.get(`/config/dropdown-masters/${masterKey}/options`, { params }),
  getRaBillLineMaster: () => api.get('/admin/config/ra-bill-line-master'),
  createRaBillLineOption: (data) => api.post('/admin/config/ra-bill-line-master/options', data),
  updateRaBillLineOption: (id, data) => api.put(`/admin/config/ra-bill-line-master/options/${id}`, data),
  deleteRaBillLineOption: (id) => api.delete(`/admin/config/ra-bill-line-master/options/${id}`),
  getRaBillLineOptions: (params) => api.get('/config/ra-bill-line-master/options', { params }),
  createConfigRule: (formId, data) => api.post(`/admin/config/forms/${formId}/rules`, data),
  updateConfigRule: (id, data) => api.put(`/admin/config/rules/${id}`, data),
  deleteConfigRule: (id, hard = false) => api.delete(`/admin/config/rules/${id}`, { params: hard ? { hard: 1 } : {} }),
  evaluateConfigRules: (formId, values) => api.post(`/config/forms/${formId}/evaluate-rules`, { values }),
  updateConfigField: (id, data) => api.put(`/admin/config/fields/${id}`, data),
  reorderConfigFields: (items) => api.post('/admin/config/fields/reorder', { items }),
  publishConfigForm: (formId, data = {}) => api.post(`/admin/config/forms/${formId}/publish`, data),
  createConfigCustomField: (formId, data) => api.post(`/admin/config/forms/${formId}/fields`, data),
  addSuggestedCustomFields: (formId) => api.post(`/admin/config/forms/${formId}/fields/suggested`),
  archiveConfigField: (id, { hard = false } = {}) => api.delete(`/admin/config/fields/${id}`, { params: hard ? { hard: 1 } : {} }),
  getCustomFieldPresets: () => api.get('/admin/config/custom-field-presets'),
  getCustomFieldValues: (entityType, entityId) => api.get(`/config/custom-values/${entityType}/${entityId}`),
  saveCustomFieldValues: (entityType, entityId, data) => api.put(`/config/custom-values/${entityType}/${entityId}`, data),
};





export const submittalsAPI = {
  getSummary: (params) => api.get('/submittals/summary', { params }),
  getAll: (params) => api.get('/submittals', { params }),
  getById: (id) => api.get(`/submittals/${id}`),
  create: (data) => api.post('/submittals', data),
  update: (id, data) => api.put(`/submittals/${id}`, data),
  delete: (id) => api.delete(`/submittals/${id}`),
  cancel: (id, data) => api.post(`/submittals/${id}/cancel`, data || {}),

  submitInternalReview: (id, data) => api.post(`/submittals/${id}/submit-internal-review`, data || {}),
  submitToConsultant: (id, data) => api.post(`/submittals/${id}/submit-to-consultant`, data || {}),
  recommendToClient: (id, data) => api.post(`/submittals/${id}/recommend-to-client`, data || {}),
  submitToClient: (id, data) => api.post(`/submittals/${id}/submit-to-client`, data || {}),
  comment: (id, data) => api.post(`/submittals/${id}/comment`, data || {}),
  approve: (id, data) => api.post(`/submittals/${id}/approve`, data || {}),
  approveWithComments: (id, data) => api.post(`/submittals/${id}/approve-with-comments`, data || {}),
  returnForRevision: (id, data) => api.post(`/submittals/${id}/return-for-revision`, data || {}),
  reject: (id, data) => api.post(`/submittals/${id}/reject`, data || {}),
  resubmit: (id, data) => api.post(`/submittals/${id}/resubmit`, data || {}),
  close: (id, data) => api.post(`/submittals/${id}/close`, data || {}),
  reassign: (id, data) => api.post(`/submittals/${id}/reassign`, data || {}),
  extendDueDate: (id, data) => api.post(`/submittals/${id}/extend-due-date`, data || {}),

  getRevisions: (id) => api.get(`/submittals/${id}/revisions`),
  getRevision: (id, revisionId) => api.get(`/submittals/${id}/revisions/${revisionId}`),
  uploadAttachment: (id, revisionId, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/submittals/${id}/revisions/${revisionId}/attachments`, fd, {
      timeout: 600000,
      onUploadProgress: (e) => {
        if (typeof onProgress === 'function' && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    });
  },
  getDownloadAttachmentUrl: (id, revisionId, attachmentId, download = false) =>
    `${API_URL}/submittals/${id}/revisions/${revisionId}/attachments/${attachmentId}/download${download ? '?download=1' : ''}`,

  getMyPending: (params) => api.get('/submittals/inbox/my-pending', { params }),
  getReturned: (params) => api.get('/submittals/inbox/returned', { params }),
  getOverdue: (params) => api.get('/submittals/inbox/overdue', { params }),

  getReportProjectRegister: (params) => api.get('/submittal-reports/project-register', { params }),
  getReportCategoryStatus: (params) => api.get('/submittal-reports/category-status', { params }),
  getReportPendingWithClient: (params) => api.get('/submittal-reports/pending-with-client', { params }),
  getReportPendingWithConsultant: (params) => api.get('/submittal-reports/pending-with-consultant', { params }),
  getReportPendingWithInternal: (params) => api.get('/submittal-reports/pending-with-internal', { params }),
  getReportOverdue: (params) => api.get('/submittal-reports/overdue', { params }),
  getReportApprovalCycleTime: (params) => api.get('/submittal-reports/approval-cycle-time', { params }),
  getReportRevisionHistory: (params) => api.get('/submittal-reports/revision-history', { params }),
  getReportClosedRegister: (params) => api.get('/submittal-reports/closed-register', { params }),

  getTransmittal: (id) => api.get(`/submittals/${id}/transmittal`),
  getApprovedPackageUrl: (id) => `${API_URL}/submittals/${id}/approved-package?download=1`,
  downloadApprovedPackage: (id) => api.get(`/submittals/${id}/approved-package`, { responseType: 'blob' }),
  generateReminders: () => api.post('/submittals/admin/generate-reminders'),
  getReportSlaDelay: (params) => api.get('/submittal-reports/sla-delay', { params }),

  getCategories: () => api.get('/submittal-categories'),
  createCategory: (data) => api.post('/submittal-categories', data),
  updateCategory: (id, data) => api.put(`/submittal-categories/${id}`, data),
  getTemplates: (params) => api.get('/submittal-templates', { params }),
  createTemplate: (data) => api.post('/submittal-templates', data),
  updateTemplate: (id, data) => api.put(`/submittal-templates/${id}`, data),
  getTemplateFields: (templateId) => api.get(`/submittal-templates/${templateId}/fields`),
  createTemplateField: (templateId, data) => api.post(`/submittal-templates/${templateId}/fields`, data),
  updateTemplateField: (id, data) => api.put(`/submittal-template-fields/${id}`, data),
  deleteTemplateField: (id) => api.delete(`/submittal-template-fields/${id}`),
  loadTemplateSampleFields: (templateId, data) => api.post(`/submittal-templates/${templateId}/load-sample-fields`, data),
};

export default api;
